import { CLOSED_STAGES } from '../../../shared/enums';
import type { IsoDate, Lead, Signal, Visit } from '../../../shared/types';
import type { StoredSignals } from '../data/signals';
import { diffDays } from './dates';
import type { LatLng } from './route';
import { metersTo } from './signalRank';

export interface FollowUp {
  lead: Lead;
  visit: Visit;
  /** 0 = due today, positive = days overdue */
  daysOverdue: number;
}

export type ContractBucket = 30 | 60 | 90;

export interface ContractItem {
  lead: Lead;
  daysLeft: number;
  bucket: ContractBucket;
}

export interface TodayModel {
  overdue: FollowUp[];
  dueToday: FollowUp[];
  contracts: ContractItem[];
}

/** Most recent visit per lead (by visited_at, then id for determinism). */
export const latestVisitByLead = (visits: readonly Visit[]): Map<string, Visit> => {
  const map = new Map<string, Visit>();
  for (const v of visits) {
    const cur = map.get(v.lead_id);
    if (!cur || v.visited_at > cur.visited_at || (v.visited_at === cur.visited_at && v.id > cur.id))
      map.set(v.lead_id, v);
  }
  return map;
};

const isClosed = (lead: Lead) => CLOSED_STAGES.includes(lead.stage);

export const bucketFor = (daysLeft: number): ContractBucket | null => {
  if (daysLeft < 0 || daysLeft > 90) return null;
  if (daysLeft <= 30) return 30;
  if (daysLeft <= 60) return 60;
  return 90;
};

/** D9: pending follow-up = latest visit's next action; closed leads excluded; contracts ≤90d, not closed_won. */
export const buildToday = (leads: readonly Lead[], visits: readonly Visit[], today: IsoDate): TodayModel => {
  const latest = latestVisitByLead(visits);
  const overdue: FollowUp[] = [];
  const dueToday: FollowUp[] = [];
  const contracts: ContractItem[] = [];

  for (const lead of leads) {
    if (!isClosed(lead)) {
      const visit = latest.get(lead.id);
      if (visit?.next_action_date && visit.next_action_date <= today) {
        const daysOverdue = diffDays(today, visit.next_action_date);
        (daysOverdue > 0 ? overdue : dueToday).push({ lead, visit, daysOverdue });
      }
    }
    if (lead.stage !== 'closed_won' && lead.contract_end_date) {
      const daysLeft = diffDays(lead.contract_end_date, today);
      const bucket = bucketFor(daysLeft);
      if (bucket) contracts.push({ lead, daysLeft, bucket });
    }
  }

  const byName = (a: { lead: Lead }, b: { lead: Lead }) =>
    a.lead.business_name.localeCompare(b.lead.business_name);
  overdue.sort((a, b) => b.daysOverdue - a.daysOverdue || byName(a, b));
  // Due today runs like a schedule: timed actions first in clock order, untimed after, then name.
  const timeKey = (f: FollowUp) => f.visit.next_action_time ?? '99:99';
  dueToday.sort((a, b) => timeKey(a).localeCompare(timeKey(b)) || byName(a, b));
  contracts.sort((a, b) => a.daysLeft - b.daysLeft || byName(a, b));
  return { overdue, dueToday, contracts };
};

export const WORTH_A_STOP = 10;

/** Worth a stop (D80): the stored copy's businesses worth a visit, nearest first when the phone knows
 *  where it is (the unplaced last), else as the server ranks them; works offline from the copy. */
export const worthAStop = (
  stored: StoredSignals | null,
  from: LatLng | null,
  limit = WORTH_A_STOP,
): { signal: Signal; meters: number | null }[] => {
  const rows = (stored?.list.signals ?? [])
    .filter((s) => s.bucket === 'top' && s.status === 'new')
    .map((signal) => ({ signal, meters: metersTo(signal, from) }));
  if (from) rows.sort((a, b) => (a.meters ?? Infinity) - (b.meters ?? Infinity));
  return rows.slice(0, limit);
};
